import React, { useEffect, useRef, useState } from "react";
import { Button, Image, Modal, Dropdown } from "react-bootstrap";

import { getItem, removeItem, setItem } from "../../utils/Functions";
import {
  deleteProfilePhoto,
  uploadOfferedCandidateProfileResume,
  uploadTemporaryFile,
} from "../../pages/Profile/actions";
import { useDispatch } from "react-redux";
import { Upload } from "antd";
import ACTION from "../../actions";

const getResumeDisplayNameKey = (userId) =>
  userId ? `resumeDisplayName_${userId}` : "resumeDisplayName";

const CustomToggle = React.forwardRef(({ children, onClick, className, style }, ref) => (
  <div
    ref={ref}
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
    className={(className || '').replace('dropdown-toggle', '')}
    style={style}
  >
    {children}
  </div>
));

const DocsUpload = ({
  resumeDetails,
  isGuest,
  type,
  photo,
  setPhoto,
  resume,
  setResume,
  setPhotoFormData,
  setResumeFormData,
  setModalShow,
  onResumeClear,
  compact = false,
}) => {
  let candidateId = JSON.parse(getItem("User"))._id;
  const dispatch = useDispatch();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [, setResumeFileName] = useState("");
  const [resumeFile, setResumeFile] = useState("");

  const [, setIsFileUploading] = useState(false);
  const [, setPreview] = useState();
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [pendingReplaceFile, setPendingReplaceFile] = useState(null);
  const [showReplaceChoiceModal, setShowReplaceChoiceModal] = useState(false);
  const [cameraModalShow, setCameraModalShow] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const uploadGenerationRef = useRef(0);
  // Keep the real file name the user picked — server stores as file_<id>.pdf
  const lastChosenFileNameRef = useRef("");

  const [, setFileList] = useState([]);
  const [, setFile] = useState();
  const [selectedFileDetails, setSelectedFileDetails] = useState(null);
  const photoFileTypes = "image/*,.jpg,.jpeg,.png,.JPG,.JPEG,.PNG";
  const resumeFileTypes = ".pdf,.doc,.docx,.PDF,.DOC,.DOCX";

  const isServerGeneratedFileName = (name = "") =>
    /^file_\d+/i.test(String(name).split("/").pop() || "");

  const persistResumeDisplayName = (name) => {
    if (!name || type !== "resume") return;
    lastChosenFileNameRef.current = name;
    setItem(getResumeDisplayNameKey(candidateId), name);
  };

  const clearPersistedResumeDisplayName = () => {
    lastChosenFileNameRef.current = "";
    removeItem(getResumeDisplayNameKey(candidateId));
  };

  const getDisplayFileName = (pathOrName, fallback = "resume.pdf") => {
    const serverName = String(pathOrName || "").split("/").pop() || fallback;
    const saved =
      lastChosenFileNameRef.current ||
      getItem(getResumeDisplayNameKey(candidateId)) ||
      "";
    if (saved && !isServerGeneratedFileName(saved)) {
      return saved;
    }
    if (
      isServerGeneratedFileName(serverName) &&
      selectedFileDetails?.name &&
      !isServerGeneratedFileName(selectedFileDetails.name)
    ) {
      return selectedFileDetails.name;
    }
    return serverName;
  };

  // Restore original resume name after leaving / revisiting the page
  useEffect(() => {
    if (type !== "resume") return;
    const saved = getItem(getResumeDisplayNameKey(candidateId));
    if (saved && !isServerGeneratedFileName(saved)) {
      lastChosenFileNameRef.current = saved;
    }
  }, [candidateId, type]);

  const getFallbackFileName = (selectedFile) => {
    if (selectedFile?.name) {
      return selectedFile.name;
    }

    const extension = selectedFile?.type?.split("/")?.[1] || "jpg";
    return type === "profile"
      ? `profile-picture.${extension}`
      : `uploaded-document.${extension}`;
  };

  const getUploadedDate = () =>
    new Date().toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  const formatBytes = (bytes, decimals = 1) => {
    if (!+bytes) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
  };

  useEffect(() => {
    if (type === "resume" && !resume) {
      setSelectedFileDetails(null);
      setPreview("");
      setFileList([]);
      setResumeFileName("");
    } else if (type === "profile" && !photo && isGuest) {
      // Only clear for guest mode; non-guest loads from resumeDetails?.photo
      setSelectedFileDetails(null);
      setPreview("");
      setFileList([]);
      setResumeFileName("");
    } else {
      setPreview(resume);
    }
  }, [resume, photo, type, isGuest]);

  // For edit profile mode: show existing photo or resume on mount
  useEffect(() => {
    if (!isGuest && resumeDetails) {
      if (type === "profile") {
        const userObj = getItem("User") ? JSON.parse(getItem("User")) : {};
        const photoPath = resumeDetails?.photo || userObj?.picture || userObj?.photo;
        if (photoPath) {
          const existingUrl = photoPath.startsWith("http")
            ? photoPath
            : (resumeDetails?.FILE_PATH ? resumeDetails.FILE_PATH + photoPath : photoPath);
          const fileName = photoPath.split("/").pop() || "profile-picture.jpg";
          setSelectedFileDetails({
            name: fileName,
            preview: existingUrl,
            uploadedDate: getUploadedDate(),
            isImage: true,
            size: "Uploaded",
          });
          setPreview(existingUrl);
        } else {
          setSelectedFileDetails(null);
          setPreview("");
        }
      } else if (type === "resume" && resumeDetails.resume) {
        const existingUrl = resumeDetails.resume?.startsWith("https://")
          ? resumeDetails.resume
          : resumeDetails.FILE_PATH + resumeDetails.resume;
        const fileName = getDisplayFileName(resumeDetails.resume, "resume.pdf");
        const ext = fileName.split(".").pop()?.toLowerCase();
        setSelectedFileDetails({
          name: fileName,
          preview: existingUrl,
          uploadedDate: getUploadedDate(),
          isImage: false,
          isPdf: ext === "pdf",
          isDoc: ["doc", "docx"].includes(ext),
          size: "Uploaded",
        });
      }
    }
  }, [resumeDetails?.photo, resumeDetails?.resume, isGuest, type]);

  useEffect(() => {
    if (cameraModalShow && cameraStream && videoRef.current) {
      videoRef.current.srcObject = cameraStream;
    }
  }, [cameraModalShow, cameraStream]);

  // const handleResumeChange = (file, event) => {
  //   setResumeFileName(file.name);
  //   let formData = new FormData();
  //   formData.append("file", file);
  //   formData.append("userId", candidateId);
  //   formData.append("type", type === "profile" ? "photo" : "resume");
  //   setResumeFile(formData);
  // };

  // const handleFileSubmit = async () => {
  //   setIsFileUploading(true);
  //   if (resumeFile !== "" && !isGuest) {
  //     dispatch(
  //       uploadOfferedCandidateProfileResume(
  //         resumeFile,
  //         candidateId,
  //         resumeDetails.resume,
  //         "resume",
  //         true,
  //         () => {
  //           setIsFileUploading(false);

  //           setResumeFile("");
  //           setResumeFileName("");
  //         }
  //       )
  //     );
  //   } else if (resumeFile !== "" && isGuest) {
  //     dispatch(
  //       uploadTemporaryFile(
  //         resumeFile,
  //         type === "profile" ? photo : resume,
  //         (response) => {
  //           setIsFileUploading(false);

  //           setResumeFile("");
  //           setResumeFileName("");
  //           type === "profile"
  //             ? setPhoto(response.data.data)
  //             : setResume(response.data.data);
  //           type === "profile"
  //             ? setPhotoFormData(resumeFile)
  //             : setResumeFormData(resumeFile);
  //           type === "profile"
  //             ? localStorage.setItem("photo", response.data.data)
  //             : localStorage.setItem("resume", response.data.data);
  //         }
  //       )
  //     );
  //   }
  // };
  const createUploadFormData = (selectedFile) => {
    let formData = new FormData();

    formData.append("file", selectedFile);
    formData.append("userId", candidateId);
    formData.append("type", type === "profile" ? "photo" : "resume");

    return formData;
  };

  const setSelectedUploadFile = (selectedFile) => {
    if (!selectedFile) {
      return "";
    }
    // Support both File objects and plain URL/string previews
    if (typeof selectedFile === "string") {
      const displayName = selectedFile.split("/").pop() || (type === "profile" ? "profile-picture.jpg" : "uploaded-document.jpg");
      const docType = displayName.split(".").pop()?.toLowerCase();
      const isImage = ["jpg", "jpeg", "png"].includes(docType);
      setPreview(selectedFile);
      setSelectedFileDetails({
        name: displayName,
        preview: selectedFile,
        uploadedDate: getUploadedDate(),
        isImage: isImage,
        size: "Uploaded",
      });
      return "";
    }

    const normalizedFile =
      selectedFile.originFileObj ||
      selectedFile.file ||
      selectedFile;
    const displayName = getFallbackFileName(normalizedFile);
    let objectUrl = "";

    const docType = normalizedFile.name?.split(".").pop()?.toLowerCase();
    const isImage =
      normalizedFile.type?.startsWith("image/") ||
      ["jpg", "jpeg", "png"].includes(docType);
    const isPdf = docType === "pdf";
    const isDoc = ["doc", "docx"].includes(docType);

    if (isImage || isPdf || isDoc) {
      try {
        objectUrl = URL.createObjectURL(normalizedFile);
        setPreview(objectUrl);
      } catch (err) {
        console.error("Preview generation failed:", err);
      }
    }

    setFile(normalizedFile);
    setResumeFileName(displayName);
    persistResumeDisplayName(displayName);
    setSelectedFileDetails({
      name: displayName,
      preview: objectUrl,
      uploadedDate: getUploadedDate(),
      isImage: isImage,
      isPdf: isPdf,
      isDoc: isDoc,
      size: formatBytes(normalizedFile.size),
    });

    const formData = createUploadFormData(normalizedFile);
    setResumeFile(formData);

    return formData;
  };

  const addResume = async (file, uploadFormData = resumeFile) => {
    if (file.type?.startsWith("image/")) {
      const objectUrl = URL.createObjectURL(file);
      setPreview(objectUrl);
    }
    setIsFileUploading(true);
    const uploadGeneration = ++uploadGenerationRef.current;

    if (uploadFormData && !isGuest) {
      // For edit profile: handle both resume and photo uploads
      const fileType = type === "profile" ? "photo" : "resume";
      const existingFilePath = type === "profile" ? resumeDetails?.photo : resumeDetails?.resume;
      dispatch(
        uploadOfferedCandidateProfileResume(
          uploadFormData,
          candidateId,
          existingFilePath,
          fileType,
          true,
          () => {
            if (uploadGeneration !== uploadGenerationRef.current) return;
            setIsFileUploading(false);
            setResumeFile("");
            setResumeFileName("");
          }
        )
      );
    } else if (uploadFormData !== "" && isGuest) {
      dispatch(
        uploadTemporaryFile(
          uploadFormData,
          type === "profile" ? photo : resume,
          (response) => {
            // Ignore stale upload if user cleared / replaced the file
            if (uploadGeneration !== uploadGenerationRef.current) return;
            setIsFileUploading(false);
            setResumeFile("");
            setResumeFileName("");
            if (type === "profile") {
              setPhoto(response.data.data);
              setPhotoFormData(uploadFormData);
              localStorage.setItem("photo", response.data.data);
            } else {
              setResume(response.data.data);
              localStorage.setItem("resume", response.data.data);
              // resumeFormData already set on select for CV auto-fill
            }
          }
        )
      );
    }
  };

  const handleProfileUploadFile = (rawFile, onSuccess) => {
    const normalizedFile = rawFile.originFileObj || rawFile.file || rawFile;
    const formData = setSelectedUploadFile(normalizedFile);
    onSuccess?.();
    addResume(normalizedFile, formData);
  };

  const handleResumeReplaceRequest = (rawFile, onSuccess) => {
    const normalizedFile = rawFile.originFileObj || rawFile.file || rawFile;
    setPendingReplaceFile(normalizedFile);
    setShowReplaceChoiceModal(true);
    onSuccess?.();
  };

  const confirmResumeReplace = (updateProfileData) => {
    if (!pendingReplaceFile) {
      setShowReplaceChoiceModal(false);
      return;
    }
    const formData = setSelectedUploadFile(pendingReplaceFile);
    if (updateProfileData && formData && setResumeFormData) {
      // Show Basic Info / Professional Details skeleton immediately while fetchInfo runs
      dispatch({ type: ACTION.START_LOADING });
      setResumeFormData(formData);
    }
    addResume(pendingReplaceFile, formData);
    setPendingReplaceFile(null);
    setShowReplaceChoiceModal(false);
  };

  const onResumeChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  const handleRemovePhoto = () => {
    if (!isGuest && candidateId) {
      dispatch(
        deleteProfilePhoto(candidateId, resumeDetails?.photo, () => {
          clearSelectedFile();
        })
      );
    } else {
      clearSelectedFile();
    }
  };

  const clearSelectedFile = () => {
    // Invalidate any in-flight upload callbacks
    uploadGenerationRef.current += 1;
    if (type === "resume") {
      clearPersistedResumeDisplayName();
    }
    setFile();
    setPreview("");
    setFileList([]);
    setResumeFileName("");
    setSelectedFileDetails(null);
    if (type === "profile") {
      setPhoto?.("");
      setPhotoFormData?.("");
      removeItem("photo");
    } else {
      setResume?.("");
      setResumeFormData?.("");
      removeItem("resume");
      // Notify parent to clear CV-parsed form data
      if (onResumeClear) onResumeClear();
    }
  };

  const stopCamera = () => {
    cameraStream?.getTracks().forEach((track) => track.stop());
    setCameraStream(null);
    setCameraModalShow(false);
  };

  const openCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: false,
      });
      setCameraStream(stream);
      setCameraModalShow(true);
    } catch (error) {
      console.error("Camera access failed", error);
    }
  };

  const captureSelfie = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas) {
      return;
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      if (!blob) {
        return;
      }

      const selfieFile = new File([blob], `profile-selfie-${Date.now()}.jpg`, {
        type: "image/jpeg",
      });
      handleProfileUploadFile(selfieFile);
      stopCamera();
    }, "image/jpeg");
  };

  return (
    <>
      {type === "resume" && (
        <div className="w-100">
          {!selectedFileDetails ? (
            <Upload
              name="file"
              multiple={false}
              showUploadList={false}
              customRequest={({ file: uploadedResume, onSuccess }) => {
                Promise.resolve().then(() => {
                  const formData = setSelectedUploadFile(uploadedResume);
                  // Kick off CV parse immediately (don't wait for temp upload)
                  if (formData && setResumeFormData) {
                    setResumeFormData(formData);
                  }
                  addResume(uploadedResume, formData);
                  onSuccess();
                });
              }}
              onChange={onResumeChange}
              accept={resumeFileTypes}
              onRemove={() => { clearSelectedFile(); }}
              className="resume-drag-upload"
            >
              <div className="resume-drag-container d-flex flex-column align-items-center justify-content-center text-center w-100">
                {/* File icon box */}
                <div className="resume-icon-box d-flex align-items-center justify-content-center border rounded-4 bg-white mb-3">
                  <i className="bx bx-upload text-secondary" style={{ fontSize: "26px" }}></i>
                </div>

                {/* Drag text */}
                <span className="resume-drag-text fw-bold mb-1">
                  Drag &amp; drop resume here
                </span>
                <span className="text-secondary mb-2" style={{ fontSize: "13px" }}>or</span>

                {/* Browse file button */}
                <Button
                  variant="light"
                  className="resume-browse-btn rounded-pill fw-medium border mb-2"
                >
                  Browse File
                </Button>

                {/* Format hint */}
                <span className="resume-formats-text text-secondary">PDF, DOC, DOCX (Max 5MB)</span>
              </div>
            </Upload>
          ) : (
            <div className="d-flex align-items-center justify-content-center px-3 py-3">
              <div
                className="d-flex align-items-center justify-content-between bg-light rounded-pill border"
                style={{ minWidth: 0, maxWidth: "680px", width: "100%", padding: "8px 18px" }}
              >
                <div className="d-flex align-items-center gap-3 overflow-hidden" style={{ minWidth: 0, flex: 1 }}>
                  <i className="bx bxs-file-pdf text-danger flex-shrink-0" style={{ fontSize: "24px" }}></i>
                  <div className="d-flex flex-column overflow-hidden" style={{ minWidth: 0 }}>
                    <span className="fw-bold text-dark text-truncate" style={{ fontSize: "14px" }} title={selectedFileDetails.name}>
                      {selectedFileDetails.name}
                    </span>
                    <span className="text-secondary" style={{ fontSize: "12px", lineHeight: "1.2" }}>{selectedFileDetails.size}</span>
                  </div>
                </div>
                <div className="d-flex gap-2 ms-2 flex-shrink-0">
                  <i className="bx bx-show text-secondary" style={{ cursor: "pointer", fontSize: "18px" }} onClick={() => setShowPreviewModal(true)}></i>
                  <Upload
                    name="file"
                    multiple={false}
                    showUploadList={false}
                    accept={resumeFileTypes}
                    customRequest={({ file: uploadedResume, onSuccess }) => {
                      Promise.resolve().then(() => {
                        handleResumeReplaceRequest(uploadedResume, onSuccess);
                      });
                    }}
                  >
                    <i className="bx bx-edit-alt text-secondary" style={{ cursor: "pointer", fontSize: "18px" }} title="Replace file"></i>
                  </Upload>
                  <i className="bx bx-x text-secondary" style={{ cursor: "pointer", fontSize: "18px" }} onClick={clearSelectedFile}></i>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {type === "profile" && (
        <div className={`profile-upload-wrapper ${compact ? "" : "w-100"}`}>
          {!selectedFileDetails ? (
            <div className={`d-flex ${compact ? "" : "flex-column align-items-center"} align-items-center`}>
              <Dropdown className="position-relative" drop="down">
                <Dropdown.Toggle
                  as={CustomToggle}
                  className="position-relative d-block"
                  style={{ cursor: "pointer" }}
                >
                  <div className={`profile-avatar-circle rounded-circle d-flex align-items-center justify-content-center bg-light border`}>
                    <i className={`bx bx-user text-secondary ${compact ? "fs-3" : "fs-1"}`}></i>
                  </div>
                  <div className="profile-camera-badge position-absolute d-flex align-items-center justify-content-center bg-primary rounded-circle">
                    <i className="bx bx-camera text-white"></i>
                  </div>
                </Dropdown.Toggle>

                <Dropdown.Menu align="end">
                  <Dropdown.Item onClick={openCamera} className="d-flex align-items-center gap-2">
                    <i className="bx bx-camera"></i> Capture
                  </Dropdown.Item>
                  <Dropdown.Item as="div" className="p-0" style={{ cursor: "pointer" }}>
                    <Upload
                      showUploadList={false}
                      accept={photoFileTypes}
                      customRequest={({ file: selectedFile, onSuccess }) => {
                        Promise.resolve().then(() => {
                          handleProfileUploadFile(selectedFile, onSuccess);
                        });
                      }}
                      className="d-block w-100"
                    >
                      <div className="dropdown-item d-flex align-items-center gap-2 w-100 text-dark m-0 bg-transparent border-0">
                        <i className="bx bx-upload"></i> Upload
                      </div>
                    </Upload>
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
              {!compact && (
                <span className="profile-avatar-text fw-semibold text-center">Profile picture</span>
              )}
            </div>
          ) : (
            <div className={`d-flex ${compact ? "" : "flex-column align-items-center"} align-items-center`}>
              <Dropdown className="position-relative" drop="down">
                <Dropdown.Toggle
                  as={CustomToggle}
                  className="position-relative d-block"
                  style={{ cursor: "pointer" }}
                >
                  <div
                    className="profile-avatar-circle rounded-circle d-flex align-items-center justify-content-center bg-light border overflow-hidden"
                    title="Click for options"
                  >
                    {selectedFileDetails.preview ? (
                      <Image src={selectedFileDetails.preview} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : (
                      <i className="bx bx-image fs-1 text-secondary"></i>
                    )}
                  </div>
                  <div className="profile-camera-badge position-absolute d-flex align-items-center justify-content-center bg-success rounded-circle">
                    <i className="bx bx-check text-white"></i>
                  </div>
                </Dropdown.Toggle>

                <Dropdown.Menu align="end">
                  <Dropdown.Item onClick={() => setShowPreviewModal(true)} className="d-flex align-items-center gap-2">
                    <i className="bx bx-show"></i> Preview
                  </Dropdown.Item>
                  <Dropdown.Item as="div" className="p-0" style={{ cursor: "pointer" }}>
                    <Upload
                      showUploadList={false}
                      accept={photoFileTypes}
                      customRequest={({ file: selectedFile, onSuccess }) => {
                        Promise.resolve().then(() => {
                          handleProfileUploadFile(selectedFile, onSuccess);
                        });
                      }}
                      className="d-block w-100"
                    >
                      <div className="dropdown-item d-flex align-items-center gap-2 w-100 text-dark m-0 bg-transparent border-0">
                        <i className="bx bx-upload"></i> Upload new
                      </div>
                    </Upload>
                  </Dropdown.Item>
                  <Dropdown.Item onClick={openCamera} className="d-flex align-items-center gap-2">
                    <i className="bx bx-camera"></i> Capture
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={handleRemovePhoto} className="d-flex align-items-center gap-2 text-danger">
                    <i className="bx bx-trash text-danger"></i> Remove
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
              {!compact && (
                <span className="profile-avatar-text fw-semibold text-center">Profile picture</span>
              )}
            </div>
          )}
        </div>
      )}

      <Modal
        show={showReplaceChoiceModal}
        centered
        onHide={() => {
          setPendingReplaceFile(null);
          setShowReplaceChoiceModal(false);
        }}
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fs-5">Replace resume</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            Do you want to update only the resume file, or also update your
            profile details based on the new resume?
          </p>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 flex-wrap justify-content-end">
          <Button
            variant="outline-secondary"
            className="px-3 rounded-pill"
            style={{ color: "#777777" }}
            onClick={() => confirmResumeReplace(false)}
          >
            Only resume
          </Button>
          <Button
            variant="primary"
            className="px-3 rounded-pill"
            onClick={() => confirmResumeReplace(true)}
          >
            Resume + profile data
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showPreviewModal} centered onHide={() => setShowPreviewModal(false)} size="lg">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fs-5">{selectedFileDetails?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center p-4">
          <div style={{ height: '60vh', backgroundColor: '#f8f9fa', borderRadius: '8px', overflow: 'hidden' }} className="d-flex align-items-center justify-content-center border">
            {selectedFileDetails?.isImage ? (
              <Image src={selectedFileDetails?.preview} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
            ) : selectedFileDetails?.isPdf ? (
              <iframe src={selectedFileDetails?.preview} title="Document Preview" style={{ width: "100%", height: "100%", border: "none" }} />
            ) : selectedFileDetails?.isDoc ? (
              <div style={{ padding: 24, textAlign: "center" }}>
                <p style={{ marginBottom: 12 }}>Preview not available for DOC/DOCX files.</p>
                <a href={selectedFileDetails?.preview} download={selectedFileDetails?.name} className="btn btn-outline-primary">Download</a>
              </div>
            ) : (
              <div style={{ padding: 24 }}>No preview available.</div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 justify-content-end">
          <Button variant="outline-secondary" className="px-4 rounded-pill" onClick={() => setShowPreviewModal(false)}>
            Close
          </Button>
          <Button variant="primary" className="px-4 rounded-pill" onClick={() => {
            clearSelectedFile();
            setShowPreviewModal(false);
          }}>
            Discard
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={cameraModalShow} centered onHide={stopCamera}>
        <Modal.Body>
          <video ref={videoRef} autoPlay playsInline muted style={{ width: "100%", borderRadius: "8px" }} />
          <canvas ref={canvasRef} className="d-none" />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={stopCamera}>
            Close
          </Button>
          <Button variant="primary" onClick={captureSelfie}>
            Capture
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DocsUpload;
